import React, { useState, useEffect } from 'react';
import { 
  getServerConfig, 
  saveServerConfig, 
  resetServerConfig, 
  validateServerConfig,
  ServerConfig 
} from '../../config/serverConfig';
import './ServerConfigModal.scss';

interface ServerConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfigChange?: () => void;
}

const ServerConfigModal: React.FC<ServerConfigModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfigChange 
}) => {
  const [config, setConfig] = useState<ServerConfig>(getServerConfig());
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setConfig(getServerConfig());
      setErrors([]);
      setSaveMessage('');
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof ServerConfig, value: string | number) => {
    setConfig(prev => ({
      ...prev,
      [field]: field === 'port' ? Number(value) : value
    }));
    setErrors([]);
    setSaveMessage('');
  };

  const handleSave = () => {
    const validation = validateServerConfig(config);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      saveServerConfig(config);
      setSaveMessage('Configuration saved successfully!');
      setErrors([]);
      
      // Notify parent component about config change
      if (onConfigChange) {
        onConfigChange();
      }
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setErrors(['Failed to save configuration']);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const defaultConfig = getServerConfig();
    setConfig(defaultConfig);
    setErrors([]);
    setSaveMessage('');
  };

  const handleClearLocalStorage = () => {
    try {
      localStorage.removeItem('serverConfig');
      setSaveMessage('✅ localStorage cleared successfully!');
      setErrors([]);
      
      // Reload the configuration from defaults
      const defaultConfig = getServerConfig();
      setConfig(defaultConfig);
      
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        // Force a page reload to apply the cleared configuration
        window.location.reload();
      }, 2000);
    } catch (error) {
      setErrors(['Failed to clear localStorage']);
    }
  };

  const handleTestConnection = async () => {
    const validation = validateServerConfig(config);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    setSaveMessage('Testing connection...');
    
    try {
      const serverUrl = `${config.protocol}://${config.host}:${config.port}`;
      const response = await fetch(`${serverUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setSaveMessage('✅ Connection successful! Server is reachable.');
        setErrors([]);
      } else {
        setSaveMessage('⚠️ Server responded but with an error status.');
        setErrors([]);
      }
    } catch (error) {
      setSaveMessage('❌ Connection failed! Server is not reachable.');
      setErrors(['Server connection failed. Please check the host and port.']);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="server-config-modal-overlay" onClick={onClose}>
      <div className="server-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="server-config-modal-header">
          <h2>Server Configuration</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="server-config-modal-content">
          <div className="config-section">
            <h3>Backend Server Settings</h3>
            <p className="description">
              Configure the address and port of your backend server. 
              The default is localhost:5555.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="protocol">Protocol:</label>
            <select
              id="protocol"
              value={config.protocol}
              onChange={(e) => handleInputChange('protocol', e.target.value)}
            >
              <option value="http">HTTP</option>
              <option value="https">HTTPS</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="host">Host:</label>
            <input
              id="host"
              type="text"
              value={config.host}
              onChange={(e) => handleInputChange('host', e.target.value)}
              placeholder="localhost"
            />
          </div>

          <div className="form-group">
            <label htmlFor="port">Port:</label>
            <input
              id="port"
              type="number"
              value={config.port}
              onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 0)}
              placeholder="5555"
              min="1"
              max="65535"
            />
          </div>

          <div className="current-url">
            <strong>Current Server URL:</strong>
            <code>{`${config.protocol}://${config.host}:${config.port}`}</code>
            <div className="storage-status">
              <small>
                {localStorage.getItem('serverConfig') 
                  ? '📝 Using localStorage configuration' 
                  : '📄 Using default configuration from file'}
              </small>
            </div>
          </div>

          {errors.length > 0 && (
            <div className="error-messages">
              {errors.map((error, index) => (
                <div key={index} className="error-message">{error}</div>
              ))}
            </div>
          )}

          {saveMessage && (
            <div className={`save-message ${saveMessage.includes('✅') ? 'success' : saveMessage.includes('❌') ? 'error' : 'info'}`}>
              {saveMessage}
            </div>
          )}

          <div className="button-group">
            <button 
              className="test-button"
              onClick={handleTestConnection}
              disabled={isSaving}
            >
              Test Connection
            </button>
            
            <button 
              className="reset-button"
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset to Defaults
            </button>
            
            <button 
              className="clear-button"
              onClick={handleClearLocalStorage}
              disabled={isSaving}
            >
              Clear localStorage
            </button>
            
            <button 
              className="save-button"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerConfigModal; 